// CartographyMap.js — Cartographie de la Testing Factory (vue d'accueil de Process).
// Affiche les macro-processus (chaîne de valeur) et leurs sous-processus de façon homogène,
// + le référentiel des rôles canoniques. Un clic sur un sous-processus ouvre son détail (modeleur).
// 100% design system + dark mode. Données : src/data/cartography/{map,roles}.json.
import React from 'react';
import { ChevronRight, Layers, GitBranch, Users } from 'lucide-react';
import { Card, CardContent, Badge, Icon } from '../ui';
import { moduleOf, CRITICALITY } from '../../theme/tokens';

// Ligne d'un sous-processus (cliquable si le fichier existe).
const SubprocessRow = ({ index, sub, info, color, onOpen }) => {
  const meta = info?.metadata || {};
  const mod = meta.module ? moduleOf(meta.module) : null;
  const crit = meta.criticality ? CRITICALITY[meta.criticality] : null;
  const steps = info ? (info.nodes || []).length : 0;
  const exists = !!info;

  return (
    <button
      type="button"
      disabled={!exists}
      onClick={() => exists && onOpen(sub.process)}
      className={`group w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
        exists
          ? 'border-gray-200 dark:border-gray-700 hover:border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer'
          : 'border-dashed border-gray-200 dark:border-gray-700 opacity-60 cursor-not-allowed'
      }`}
    >
      <span
        className="h-7 w-7 shrink-0 rounded-md flex items-center justify-center text-xs font-bold text-white"
        style={{ backgroundColor: color }}
      >
        {index}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">{sub.title}</span>
        <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
          {mod && <Badge color={mod.color}><Icon name={mod.icon} className="h-3 w-3" />{mod.label}</Badge>}
          {crit && <Badge color={crit.color}>{meta.criticality}</Badge>}
          {exists ? (
            <span className="text-[11px] text-gray-400 dark:text-gray-500">{steps} étapes</span>
          ) : (
            <span className="text-[11px] italic text-gray-400 dark:text-gray-500">à venir</span>
          )}
        </span>
      </span>
      {exists && (
        <ChevronRight className="h-4 w-4 shrink-0 text-gray-300 group-hover:text-gray-500 dark:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
      )}
    </button>
  );
};

// Carte d'un macro-processus.
const MacroCard = ({ macro, byFile, onOpen }) => (
  <Card className="flex flex-col overflow-hidden">
    <div className="h-1.5 w-full" style={{ backgroundColor: macro.color }} />
    <CardContent className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <span
          className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${macro.color}1A`, color: macro.color }}
        >
          <Icon name={macro.icon} className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold tracking-wide" style={{ color: macro.color }}>{macro.id}</span>
            <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight">{macro.title}</h3>
          </div>
        </div>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{macro.description}</p>
      <div className="flex flex-col gap-1.5">
        {macro.subprocesses.map((sub, i) => (
          <SubprocessRow
            key={sub.process}
            index={i + 1}
            sub={sub}
            info={byFile[sub.process]}
            color={macro.color}
            onOpen={onOpen}
          />
        ))}
      </div>
    </CardContent>
  </Card>
);

// Référentiel des rôles (acteurs canoniques) — humains vs système.
const RolesReference = ({ roles }) => {
  const entries = Object.entries(roles || {});
  const humans = entries.filter(([, r]) => r.category === 'human');
  const systems = entries.filter(([, r]) => r.category === 'system');

  const Group = ({ icon: GIcon, title, items }) => (
    <div className="flex-1 min-w-[260px]">
      <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
        <GIcon className="h-3.5 w-3.5" />{title}
      </div>
      <div className="space-y-1.5">
        {items.map(([id, r]) => (
          <div key={id} className="flex items-start gap-2">
            <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: r.color }} />
            <div className="min-w-0">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{r.label}</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">{r.responsibility}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Card>
      <CardContent>
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-blue-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Référentiel des rôles</h3>
          <Badge>{entries.length} acteurs</Badge>
        </div>
        <div className="flex flex-wrap gap-6">
          <Group icon={Users} title="Rôles humains" items={humans} />
          <Group icon={Layers} title="Acteurs système ScanBank" items={systems} />
        </div>
      </CardContent>
    </Card>
  );
};

const CartographyMap = ({ cartography, byFile, roles, onOpen }) => {
  const macros = cartography.macros || [];
  const transverse = cartography.transverse;
  const allSubs = [...macros.flatMap((m) => m.subprocesses), ...(transverse?.subprocesses || [])];
  const total = allSubs.length;
  const available = allSubs.filter((s) => byFile[s.process]).length;

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <GitBranch className="h-6 w-6 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{cartography.title}</h1>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300 max-w-3xl">{cartography.description}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge color="#3B82F6">{macros.length} macro-processus</Badge>
          <Badge color="#10B981">{available} / {total} sous-processus modélisés</Badge>
          <Badge color="#8B5CF6">{Object.keys(roles || {}).length} acteurs</Badge>
        </div>
      </div>

      {/* Référentiel des rôles */}
      <RolesReference roles={roles} />

      {/* Macro-processus */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
        {macros.map((m) => (
          <MacroCard key={m.id} macro={m} byFile={byFile} onOpen={onOpen} />
        ))}
      </div>

      {/* Transverse */}
      {transverse && (
        <Card className="overflow-hidden">
          <div className="h-1.5 w-full" style={{ backgroundColor: transverse.color }} />
          <CardContent>
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
              <div className="flex items-center gap-3 md:w-1/3">
                <span
                  className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${transverse.color}1A`, color: transverse.color }}
                >
                  <Icon name={transverse.icon} className="h-5 w-5" />
                </span>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold tracking-wide" style={{ color: transverse.color }}>{transverse.id}</span>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight">{transverse.title}</h3>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{transverse.description}</p>
                </div>
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {transverse.subprocesses.map((sub, i) => (
                  <SubprocessRow
                    key={sub.process}
                    index={i + 1}
                    sub={sub}
                    info={byFile[sub.process]}
                    color={transverse.color}
                    onOpen={onOpen}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CartographyMap;
